import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import EraserBrush from 'components/common/EraserBrush';
import useMemoSelector from 'hooks/useMemoSelector';
import { getBatchEraserItem } from 'store/batch/selectors';
import loadingImage from 'utils/images/loadingImage';
import useEraserSizes from 'components/common/EraserBrush/useEraserSizes';
import { invertMask } from 'components/common/EraserBrush/utils';
import batchSlice from 'store/batch';
import useBatchData from 'pages/Batch/sections/Context/useBatchData';
import { ActionTypes } from 'pages/Batch/sections/Context/types';
import Toolchain from 'pages/Batch/utils/process/toolchain';
import { MaskType, IBatchData } from 'types';
import errorLogger from 'services/errorLogger';
import useUploadNotification from 'pages/Batch/utils/useUploadNotification';
import { BATCH_ERROR, BATCH_TOOLS, IMAGE_MIME_TYPES } from 'pages/Batch/configs';
import { GET_TRANSPARENT_BG_URL } from 'configs/constants';
import { getIsDarkModeEnabled } from 'store/selectors/general';
import analytics, { SubToolNames } from 'pages/Batch/utils/analytics';

function BatchEraser() {
  const dispatch = useDispatch();
  const { dispatchData } = useBatchData();
  const eraserItem = useMemoSelector(getBatchEraserItem);
  const { showUploaderError } = useUploadNotification();
  const isDarkModeEnabled = useMemoSelector(getIsDarkModeEnabled);

  const [passedImage, setPassedImage] = useState<HTMLImageElement>();
  const [emptyImage, setEmptyImage] = useState<HTMLImageElement>();
  const [defaultMask, setDefaultMask] = useState<HTMLCanvasElement | HTMLImageElement>();

  const [width, height] = useEraserSizes(passedImage);

  useEffect(() => {
    if (eraserItem && !passedImage) {
      const getPassedImage = async () => {
        try {
          const batchImage = eraserItem.result;
          const image = await loadingImage(batchImage.src);
          setPassedImage(image);
        } catch (error) {
          showUploaderError(BATCH_ERROR.serverError);
          errorLogger.trigger(error, 'error');
        }
      };
      getPassedImage();
    }
  }, [eraserItem, height, passedImage, width, showUploaderError]);

  useEffect(() => {
    if (eraserItem?.pendingResult && width && height) {
      const getDefaultMask = async () => {
        const { pendingResult } = eraserItem;
        if (pendingResult?.src) {
          try {
            const maskImage = await loadingImage(pendingResult.src);
            const mask = invertMask(maskImage, passedImage, width, height);
            mask && setDefaultMask(mask);
          } catch (err) {
            showUploaderError(BATCH_ERROR.serverError);

            errorLogger.trigger(err, 'error');
          }
        }
      };

      getDefaultMask();
    }
  }, [eraserItem, height, passedImage, width, showUploaderError]);

  useEffect(() => {
    const getEmptyImage = async () => {
      try {
        const empty = await loadingImage(GET_TRANSPARENT_BG_URL(isDarkModeEnabled));
        setEmptyImage(empty);
      } catch (err) {
        showUploaderError(BATCH_ERROR.serverError);
        errorLogger.trigger(err, 'error');
      }
    };

    getEmptyImage();
  }, [showUploaderError, isDarkModeEnabled]);

  const handleClose = useCallback(() => {
    dispatch(batchSlice.actions.setEraserItem(null));
  }, [dispatch]);

  const putData = useCallback((item: IBatchData) => {
    dispatchData({
      type: ActionTypes.PUT_DATA,
      item,
    });
  }, [dispatchData]);

  const handleDone = useCallback((item: IBatchData) => {
    putData(item);
    handleClose();
  }, [handleClose, putData]);

  const handleApply = useCallback(async (mask: MaskType, { mode, settings }) => {
    if (eraserItem && mask) {
      analytics.utils().setSubTool(BATCH_TOOLS.removeBG, {
        name: SubToolNames.eraser,
        value: { ...(mode && { mode }), ...settings },
      });
      const resultMask = invertMask(mask, passedImage, width, height);
      const toolchain = new Toolchain();
      const processFn = () => new Promise<{blob: Blob; result: string}>(resolve => {
        (resultMask as HTMLCanvasElement).toBlob(blob => {
          blob && resolve({ blob, result: URL.createObjectURL(blob) });
        }, IMAGE_MIME_TYPES.WEBP);
      });
      await toolchain.processItem({ data: eraserItem, processFn, callback: handleDone });
    }
  }, [eraserItem, handleDone, height, passedImage, width]);

  if (eraserItem?.pendingResult && !defaultMask) {
    return null;
  }

  return (
    <EraserBrush
      width={width}
      height={height}
      onCancel={handleClose}
      onApply={handleApply}
      isPremiumImage={!!eraserItem?.isProcessed}
      defaultMask={defaultMask}
      hasPersonCutout={false}
      effectImage={passedImage}
      originalImage={emptyImage}
      isDarkMode
    />
  );
}

export default BatchEraser;
